import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { isInventoriableProduct } from '../stock/public-api';
import type {
  InventoryFlowResult,
  InventoryUserNotice,
  ReceiveStockForm,
  ReceiveStockInput,
} from './inventoryOperations.types';
import { stockQuantityAllowsFraction } from './inventoryUtils';

const READ_ONLY_NOTICE: InventoryUserNotice = {
  tone: 'info',
  title: 'Solo lectura',
  message: 'Podés ver el stock, no ingresar mercadería.',
};

export function parseReceiveStockForm(
  ctx: { canEdit: boolean; branchId: string | null },
  form: ReceiveStockForm
): InventoryFlowResult<ReceiveStockInput> {
  if (!ctx.canEdit) {
    return { kind: 'blocked', notice: READ_ONLY_NOTICE };
  }
  if (!ctx.branchId) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Elegí una sucursal',
        message: 'Seleccioná una sucursal para cargar mercadería.',
      },
    };
  }
  if (!form.product) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Elegí un producto',
        message: 'Elegí un producto del catálogo para cargarlo en esta sucursal.',
      },
    };
  }
  if (!isInventoriableProduct(form.product)) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Servicio sin stock',
        message: 'Los servicios no llevan stock.',
      },
    };
  }

  const allowsFraction = stockQuantityAllowsFraction(form.product.unitOfMeasure);
  const quantity = parseDecimalInput(String(form.entryQuantity), {
    integerOnly: !allowsFraction,
    min: allowsFraction ? 0.0001 : 1,
  });
  const unitCost = parseDecimalInput(String(form.entryUnitCost), { min: 0 });
  const minQuantity = parseDecimalInput(String(form.entryMinQuantity || '0'), {
    integerOnly: !allowsFraction,
    min: 0,
  });

  if (quantity === null) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Cantidad inválida',
        message: allowsFraction
          ? 'Ingresá una cantidad mayor a cero (ej. 0,5 o 2).'
          : 'Ingresá una cantidad entera mayor a cero.',
      },
    };
  }
  if (unitCost === null) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Costo unitario inválido',
        message: 'Ingresá el precio al que lo comprás (ej. 120 o 120,50). No lo dejes vacío.',
      },
    };
  }
  if (minQuantity === null) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Stock mínimo inválido',
        message: allowsFraction ? 'Ingresá un mínimo de 0 o más.' : 'Ingresá un mínimo entero de 0 o más.',
      },
    };
  }
  if (form.entryExpiry && !/^\d{4}-\d{2}-\d{2}$/.test(form.entryExpiry.trim())) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Fecha inválida',
        message: 'La fecha de vencimiento no es válida.',
      },
    };
  }

  return {
    kind: 'done',
    refresh: { stocks: true, branchActivity: true, dashboard: true },
    data: {
      branchId: ctx.branchId,
      product: form.product,
      quantity: allowsFraction ? quantity : Math.round(quantity),
      unitCost,
      minQuantity: allowsFraction ? minQuantity : Math.round(minQuantity),
      expiresAt: form.entryExpiry.trim() || null,
      lotNumber: form.entryLotNumber.trim() || null,
      reason: form.entryReason,
      performedByUserId: null,
      supplierId: form.entrySupplierId,
      variantId: form.entryVariantId ?? null,
    },
  };
}
